import { randomKey } from "@sanity/util/content";
import { match, P } from "ts-pattern";
import { SUPPORTED_LANGUAGE_IDS } from "../common/localization";
import { MultilingualArrayBlockContentValue } from "../schemas/base/multilingualArrayBlockContent";
import { MultilingualArrayStringValue } from "../schemas/base/multilingualArrayString";
import { MultilingualArrayTextValue } from "../schemas/base/multilingualArrayText";
import { MultilingualBlockContentValue } from "../schemas/base/multilingualBlockContent";
import { MultilingualStringValue } from "../schemas/base/multilingualString";
import { MultilingualTextValue } from "../schemas/base/multilingualText";
import { isPresent } from "../utils/checks";

type MultilingualObjectTypes =
  | MultilingualStringValue
  | MultilingualTextValue
  | MultilingualBlockContentValue;

type MultilingualObjectArrayTypes =
  | MultilingualArrayStringValue
  | MultilingualArrayTextValue
  | MultilingualArrayBlockContentValue;

type InternationalizedArrayTypes =
  | "internationalizedArrayStringValue"
  | "internationalizedArrayTextValue"
  | "internationalizedArrayBlockContentValue";

export function toMultilingualArrayValue(value: undefined): undefined;
export function toMultilingualArrayValue(
  value: MultilingualStringValue | undefined
): MultilingualArrayStringValue | undefined;
export function toMultilingualArrayValue(
  value: MultilingualTextValue | undefined
): MultilingualArrayTextValue | undefined;
export function toMultilingualArrayValue(
  value: MultilingualBlockContentValue | undefined
): MultilingualArrayBlockContentValue | undefined;
export function toMultilingualArrayValue(
  value: unknown | undefined
): unknown | undefined;
export function toMultilingualArrayValue(
  value: MultilingualObjectTypes | unknown | undefined
): MultilingualObjectArrayTypes | undefined | unknown {
  return match(value)
    .returnType<MultilingualObjectArrayTypes | undefined | unknown>()
    .with({ _type: "multilingualString" }, (v) => {
      return {
        _type: "multilingualArrayString" as const,
        langs: extractLanguages<MultilingualArrayStringValue>(
          v,
          "internationalizedArrayStringValue"
        )
      } satisfies MultilingualArrayStringValue;
    })
    .with({ _type: "multilingualText" }, (v) => {
      return {
        _type: "multilingualArrayText" as const,
        langs: extractLanguages<MultilingualArrayTextValue>(
          v,
          "internationalizedArrayTextValue"
        )
      } satisfies MultilingualArrayTextValue;
    })
    .with({ _type: "multilingualBlockContent" }, (v) => {
      return {
        _type: "multilingualArrayBlockContent" as const,
        langs: extractLanguages<MultilingualArrayBlockContentValue>(
          v,
          "internationalizedArrayBlockContentValue",
          []
        )
      } satisfies MultilingualArrayBlockContentValue;
    })
    .with(P.nullish, () => undefined)
    .otherwise((v) => {
      const guessedType = guessType(v);
      if (guessedType === "multilingualString") {
        return {
          _type: "multilingualArrayString" as const,
          langs: extractLanguages<MultilingualArrayStringValue>(
            { ...(v as any), _type: "multilingualString" },
            "internationalizedArrayStringValue"
          )
        } satisfies MultilingualArrayStringValue;
      }

      if (guessedType === "multilingualBlockContent") {
        console.info("guessed block content");
        return {
          _type: "multilingualArrayBlockContent" as const,
          langs: extractLanguages<MultilingualArrayBlockContentValue>(
            { ...(v as any), _type: "multilingualBlockContent" },
            "internationalizedArrayBlockContentValue",
            []
          )
        } satisfies MultilingualArrayBlockContentValue;
      }

      return v;
    });
}

export const guessType = (
  value: any
): MultilingualObjectTypes["_type"] | undefined => {
  const firstLang = Object.keys(value)[0];
  const firstValue = value[firstLang];
  if (typeof firstValue === "string") return "multilingualString";
  if (typeof firstValue === "object") return "multilingualBlockContent";
  return undefined;
};

const extractLanguages = <T extends MultilingualObjectArrayTypes>(
  value: MultilingualObjectTypes,
  type: InternationalizedArrayTypes,
  defaultValue?: NonNullable<T["langs"]>[0]["value"]
): NonNullable<T["langs"]> => {
  return SUPPORTED_LANGUAGE_IDS.map((lang) => {
    const localizedValue = value[lang];
    if (!isPresent(localizedValue)) {
      if (defaultValue) {
        return {
          _type: type,
          _key: randomKey(),
          language: lang,
          value: defaultValue
        } as any;
      } else return undefined;
    }

    return {
      _type: type,
      _key: randomKey(),
      language: lang,
      value: localizedValue
    } as any;
  }).filter(isPresent);
};
